/// Score band taxonomy used across every Pulse surface. A 0..10 health
/// score maps to one of five visual bands. Insufficient-data (gate not
/// met) renders as the `empty` band with a "—" sentinel.
///   0.0-4.0    low       (danger red)
///   4.1-6.0    midLow    (warning amber)
///   6.1-7.5    midHigh   (mood-4 yellow-green)
///   7.6-10.0   high      (success green)
///   null       empty     (text-quaternary)

export type ScoreBand = "low" | "midLow" | "midHigh" | "high" | "empty";

export function bandOf(score: number | null | undefined): ScoreBand {
  if (score == null) return "empty";
  if (score <= 4.0) return "low";
  if (score <= 6.0) return "midLow";
  if (score <= 7.5) return "midHigh";
  return "high";
}

interface BandClasses {
  fg: string;
  bg: string;
  ring: string;
  dot: string;
}

/// Tailwind utility strings keyed by band. Every consumer composes these
/// instead of writing color classes directly, keeps drift in one place.
export const BAND_CLASSES: Record<ScoreBand, BandClasses> = {
  low: {
    fg: "text-danger-fg",
    bg: "bg-danger-bg",
    ring: "ring-danger-fg/30",
    dot: "bg-danger-fg",
  },
  midLow: {
    fg: "text-warning-fg",
    bg: "bg-warning-bg",
    ring: "ring-warning-fg/30",
    dot: "bg-warning-fg",
  },
  midHigh: {
    fg: "text-mood-4",
    bg: "bg-mood-4/10",
    ring: "ring-mood-4/30",
    dot: "bg-mood-4",
  },
  high: {
    fg: "text-success-fg",
    bg: "bg-success-bg",
    ring: "ring-success-fg/30",
    dot: "bg-success-fg",
  },
  empty: {
    fg: "text-text-quaternary",
    bg: "bg-surface-muted",
    ring: "ring-border-base",
    dot: "bg-text-quaternary",
  },
};

/// Words for the bands, used by `BandLegend` and score-chip hover text.
/// `empty` reads "Not enough data" rather than "Hidden": the score is not
/// being withheld from this particular reader, there genuinely are not
/// enough responses yet for one to exist, and "Hidden" implies a control
/// somewhere that would reveal it.
export const BAND_LABEL: Record<ScoreBand, string> = {
  low: "Low",
  midLow: "Mid-low",
  midHigh: "Mid-high",
  high: "High",
  empty: "Not enough data",
};

/// Map a band to a stroke color usable in inline SVG (e.g. trend lines).
/// Mirrors `BAND_CLASSES[].dot` but resolves to a CSS color token so the
/// SVG `stroke` attribute can consume it directly.
export const BAND_STROKE: Record<ScoreBand, string> = {
  low: "var(--pw-danger-fg)",
  midLow: "var(--pw-warning-fg)",
  midHigh: "var(--pw-mood-4)",
  high: "var(--pw-success-fg)",
  empty: "var(--text-quaternary)",
};
